import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  MCP_FILTERABLE_FIELDS,
  type MCPServerListItem,
} from '@kirin/api-client/contracts/mcp';
import {
  MCP_FILTER_URL_KEYS,
  MCP_TYPE_OPTIONS,
  MCP_TRANSPORT_OPTIONS,
  MCP_STATUS_OPTIONS,
} from './constants';
import {
  NameCell,
  VersionCell,
  PublisherCell,
  TypeCell,
  TransportCell,
  UsersCell,
  StatusCell,
  FirstSeenCell,
  LastSeenCell,
  ToolsCell,
} from './CellRenderers';
import {
  MCPUserFilterOption,
  MCPTypeFilterOption,
  MCPTransportFilterOption,
  MCPStatusFilterOption,
  MCPPublisherFilterOption,
  MCPVersionFilterOption,
} from './FilterComponents';
import { fetchMCPFieldFilterOptions } from './api';
import { queryKeys } from '@/hooks/api/queryKeys';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

export function createMCPColumns(
  organizationId: string,
  onSelectServer?: (serverName: string) => void
): ColumnConfig<MCPServerListItem>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) => <NameCell row={row} />,
      width: 280,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'name', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'version',
      header: 'Version',
      accessor: 'versions',
      renderCell: ({ row }) => <VersionCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'version',
      sortKey: 'v',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.VERSION],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'version', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'version', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPVersionFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'publisher',
      header: 'Publisher',
      accessor: 'publisher',
      renderCell: ({ row }) => <PublisherCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'publisher',
      sortKey: 'pub',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.PUBLISHER],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'publisher', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'publisher', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPPublisherFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'type',
      renderCell: ({ row }) => <TypeCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'type',
      sortKey: 'ty',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TYPE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TYPE_OPTIONS,
        render: (option, isSelected) => (
          <MCPTypeFilterOption type={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'transport',
      header: 'Transport',
      accessor: 'transport',
      renderCell: ({ row }) => <TransportCell row={row} />,
      width: 200,
      sortable: true,
      sortField: 'transport',
      sortKey: 'tr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TRANSPORT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TRANSPORT_OPTIONS,
        render: (option, isSelected) => (
          <MCPTransportFilterOption
            transport={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'userCount',
      renderCell: ({ row }) => (
        <UsersCell row={row} onSelectServer={onSelectServer} />
      ),
      width: 100,
      sortable: true,
      sortField: 'userCount',
      sortKey: 'uc',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.USERS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'users', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'users', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPUserFilterOption
            userId={option.value}
            userName={(option.metadata?.name as string | null) ?? null}
            userEmail={(option.metadata?.email as string | null) ?? null}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'tools',
      header: 'Tools',
      accessor: 'tools',
      renderCell: ({ row }) => <ToolsCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'tools',
      sortKey: 'to',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TOOLS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.mcp.filterOptions(organizationId, 'tools', params),
        queryFn: params =>
          fetchMCPFieldFilterOptions(organizationId, 'tools', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'runningCount',
      renderCell: ({ row }) => <StatusCell row={row} />,
      width: 110,
      sortable: true,
      sortField: 'runningCount',
      sortKey: 'rc',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.STATUS],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_STATUS_OPTIONS,
        render: (option, isSelected) => (
          <MCPStatusFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First Seen',
      accessor: 'firstSeenAt',
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.LAST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
